import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { MessageCircle } from 'lucide-react';
import { useCoachPanelContext } from './CoachPanelProvider';
import { isCoachHiddenRoute } from './coachRouteUtils';
import { cn } from '@/lib/utils';

const TOOLTIP_KEY = 'coach-fab-tooltip-shown';

export function CoachFAB() {
  const { open, isOpen } = useCoachPanelContext();
  const location = useLocation();
  const [showTooltip, setShowTooltip] = useState(false);

  // Show tooltip once for new users after 1.5s delay
  useEffect(() => {
    if (!localStorage.getItem(TOOLTIP_KEY)) {
      const timer = setTimeout(() => setShowTooltip(true), 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  // Auto-dismiss tooltip after 5 seconds
  useEffect(() => {
    if (showTooltip) {
      const timer = setTimeout(() => dismissTooltip(), 5000);
      return () => clearTimeout(timer);
    }
  }, [showTooltip]);

  // Dismiss on route change
  useEffect(() => {
    if (showTooltip) dismissTooltip();
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  // Dismiss on any outside click
  useEffect(() => {
    if (showTooltip) {
      const handler = () => dismissTooltip();
      document.addEventListener('click', handler, { once: true });
      return () => document.removeEventListener('click', handler);
    }
  }, [showTooltip]);

  const dismissTooltip = () => {
    setShowTooltip(false);
    localStorage.setItem(TOOLTIP_KEY, 'true');
  };

  const handleClick = () => {
    dismissTooltip();
    open();
  };

  // Hide on public (unauthenticated) and admin pages
  if (isCoachHiddenRoute(location.pathname) || isOpen) return null;

  return (
    <div className="fixed bottom-6 right-6 z-40 flex flex-col items-end gap-2">
      {showTooltip && (
        <div
          className="rounded-lg bg-foreground text-background px-3 py-2 text-sm shadow-lg cursor-pointer max-w-[220px] animate-in fade-in slide-in-from-bottom-2"
          onClick={(e) => { e.stopPropagation(); dismissTooltip(); }}
        >
          Your AI coach is here — tap to start a conversation
        </div>
      )}
      <button
        onClick={handleClick}
        className={cn(
          'h-12 w-12 rounded-full bg-primary shadow-lg hover:shadow-xl',
          'transition-all flex items-center justify-center',
          'hover:scale-105 active:scale-95',
          showTooltip && 'animate-pulse',
        )}
        aria-label="Open AI Coach"
      >
        <MessageCircle className="h-6 w-6 text-primary-foreground" />
      </button>
    </div>
  );
}
